'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { getExperimentOverrides } from '#pplx/frontend/apps/ask/backend-flag-override/experimentOverrides.js';
import { LimitedFreeTierGateModalDynamic } from '#pplx/frontend/apps/ask/billing/checkout/modals/LimitedFreeTierGateModalDynamic.js';
import { getBootstrapConfig } from '#pplx/frontend/apps/ask/bootstrap-config/bootstrapConfig.js';
import { useCometMcpServers } from '#pplx/frontend/apps/ask/comet-browser/hooks/useCometMcpServers.js';
import { useCometState, useEntropyBrowser, } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { useMaxFileUpload } from '#pplx/frontend/apps/ask/hooks/eppo-hooks/useMaxFileUpload.js';
import { useLimitedFreeTierExperience } from '#pplx/frontend/apps/ask/hooks/limited-free-tier-gate-hooks/useLimitedFreeTierExperience.js';
import { useRefuseViewerSubmission } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/useRefuseViewerSubmission.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useIsIncognito } from '#pplx/frontend/apps/ask/incognito/useIsIncognito.js';
import { viewerAskDenialMessage } from '#pplx/frontend/apps/ask/organization/utils/askAdmissionError.js';
import { useFollowUps } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/useFollowUps.js';
import { useInjectSearchResults } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/useInjectSearchResults.js';
import { PplxQueryStateContext } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/usePerplexityQueryState.js';
import { useWebBrowserExtensionAvailable } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/useWebBrowserExtensionAvailable.js';
import { postFinalResponseClientSideLogic } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/utils/postFinalResponseClientSideLogic.js';
import { useRWToken } from '#pplx/frontend/apps/ask/providers/hooks/useRWToken.js';
import { useResults, useResultsActions } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useInitialConfiguredModel } from '#pplx/frontend/apps/ask/providers/useInitialConfiguredModel.js';
import { shouldRunNavigationalSearch } from '#pplx/frontend/apps/ask/queries/navigational-queries/shouldRunNavigationalSearch.js';
import { getHomepageUpsellsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/upsellKeys.js';
import { useUserCapabilities } from '#pplx/frontend/apps/ask/queries/user-queries/useUserCapabilities.js';
import { useIncrementVisitorDailyQueryCount } from '#pplx/frontend/apps/ask/queries/visitor-queries/useGetVisitorInformation.js';
import { useSearchModeForSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useSearchModeForSearchModel.js';
import { getMergedPrefills } from '#pplx/frontend/apps/ask/skills/getMergedPrefills.js';
import { useUserMentionAccessToast } from '#pplx/frontend/apps/ask/user-mentions/useUserMentionAccessToast.js';
import { useDecrementFreeQueries, useFreeQueries, } from '#pplx/frontend/apps/ask/user-settings/hooks/useFreeQueries.js';
import { useUserSettingsQuery } from '#pplx/frontend/apps/ask/user-settings/hooks/useUserSettingsQuery.js';
import { useSubmitQuery } from '#pplx/frontend/ask-query-submission/hooks/useSubmitQuery.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { formatFollowUps, serializeFollowUps, } from '#pplx/frontend/components/search/markdown/utils/quoteUtils.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { getLoggedExperimentAssignments } from '#pplx/frontend/experimentation/assignmentStore.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useSingularAttribution } from '#pplx/frontend/growth/analytics-singular/SingularAttributionProviderShared.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
export const AskQueryStateProvider = ({ children, resetKey, shouldFetchSettings = false, }) => {
    const reason = 'ask-query-state-provider';
    const renderingPlace = getBootstrapConfig().erp;
    const { session } = useSession();
    const { $t } = useIntl();
    const { isViewer } = useIsSubscribed();
    const refuseViewerSubmission = useRefuseViewerSubmission(isViewer ? $t(viewerAskDenialMessage) : undefined, ModalOrigin.TRY_PRO);
    const { track } = useInstrumentation();
    // Adapt the bridge `track` to `useSubmitQuery`'s overloaded `SubmitQueryTrackEvent` (name, data)
    // contract — a shared cross-app hook, so we adapt at this boundary rather than re-typing its param.
    const trackEvent = useCallback((name, data) => track({ event: name, data: (data ?? {}) }), [track]);
    const entropyBrowser = useEntropyBrowser();
    const followUps = useFollowUps();
    const { selections, reset: resetQuotes } = followUps;
    const quote = useMemo(() => formatFollowUps(selections), [selections]);
    const quoteForSubmission = useMemo(() => (selections.some((item) => item.anchor) ? serializeFollowUps(selections) : quote), [quote, selections]);
    const [quoteAttachment, setQuoteAttachment] = useState();
    const setQuote = useCallback((text, attachment) => {
        resetQuotes(text ? [{ id: safeRandomUUID(), text }] : []);
        setQuoteAttachment(text ? attachment : undefined);
    }, [resetQuotes]);
    const selectedSearchMode = useSelectedSearchMode();
    const getSearchModeForSearchModel = useSearchModeForSearchModel();
    const { sources: sourcesFromContext, configuredModel, askDeepResearchModel, compareModels, setConfiguredModel, } = useThreadConfig();
    const { model: initialConfiguredModel } = useInitialConfiguredModel();
    useUserCapabilities();
    const rwToken = useRWToken();
    const { firstResult, lastResult } = useResults();
    const { setCurrentStreamId } = useResultsActions();
    const { hasAiProfile: isPersonalized } = useUserSettingsQuery({
        enabled: shouldFetchSettings,
        reason,
    });
    const { data: freeQueries } = useFreeQueries({ reason });
    const decrementFreeQueries = useDecrementFreeQueries();
    const incrementVisitorDailyQueryCount = useIncrementVisitorDailyQueryCount();
    const { variation: limitedFreeTierExperience } = useLimitedFreeTierExperience({
        freeQueries,
    });
    const isUserLoggedIn = useIsUserLoggedIn();
    const { isIncognito } = useIsIncognito({ reason });
    const [submissionQueryCount, setSubmissionQueryCount] = useState(0);
    const previousResetKeyRef = useRef(resetKey);
    useLayoutEffect(() => {
        if (resetKey === undefined || previousResetKeyRef.current === resetKey)
            return;
        previousResetKeyRef.current = resetKey;
        setQuote(null);
        setSubmissionQueryCount(0);
    }, [resetKey, setQuote]);
    const { sendQueryEventSingular, maybeSendNthQueryEventSingular } = useSingularAttribution();
    const cometState = useCometState();
    const { cometMcpDisabledServers } = useCometMcpServers();
    const maxAttachmentsLimit = useMaxFileUpload();
    const injectSearchResults = useInjectSearchResults();
    const queryClient = useQueryClient();
    const { openModal } = useModalContext();
    const openUserMentionAccessToast = useUserMentionAccessToast();
    const refetchHomepageUpsells = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: getHomepageUpsellsQueryKey(),
        });
    }, [queryClient]);
    const openLimitedFreeTierGateModal = useCallback((variant) => {
        openModal(LimitedFreeTierGateModalDynamic, {
            variant,
            origin: ModalOrigin.LIMITED_FREE_TIER_RATE_LIMITED,
        }, { replace: true });
    }, [openModal]);
    const isWebBrowserExtensionAvailable = useWebBrowserExtensionAvailable();
    const submitQueryBase = useSubmitQuery({
        cometMcpDisabledServers,
        cometState,
        compareModels,
        configuredModel,
        askDeepResearchModel,
        decrementFreeQueries,
        entropyBrowser,
        firstResult,
        freeQueries,
        getExperimentOverrides,
        getExperimentAssignments: getLoggedExperimentAssignments,
        getMergedPrefills,
        getSearchModeForSearchModel,
        initialConfiguredModel,
        incrementVisitorDailyQueryCount,
        injectSearchResults,
        isIncognito,
        isPersonalized,
        lastResult,
        limitedFreeTierExperience,
        maxAttachmentsLimit,
        openLimitedFreeTierGateModal,
        quote: quoteForSubmission,
        reason,
        renderingPlace,
        rwToken,
        selectedSearchMode,
        setConfiguredModel,
        setCurrentStreamId,
        setQuote,
        setSubmissionQueryCount,
        shouldRunNavigationalSearch,
        skipSearchEnabled: true,
        sourcesFromContext,
        trackEvent,
    });
    const submitQuery = useCallback((params) => {
        if (refuseViewerSubmission())
            return false;
        return submitQueryBase({
            ...params,
            ...(isWebBrowserExtensionAvailable && { isWebBrowserExtensionAvailable }),
            analyticsPolicy: quoteForSubmission ? 'redact-query-content' : params.analyticsPolicy,
            onFirstMessageReceived: (message) => {
                if (params.mentions) {
                    openUserMentionAccessToast({
                        entryUUID: message.backend_uuid,
                        mentions: params.mentions,
                        threadSlug: message.slug,
                    });
                }
                params.onFirstMessageReceived?.(message);
            },
        });
    }, [
        isWebBrowserExtensionAvailable,
        openUserMentionAccessToast,
        quoteForSubmission,
        refuseViewerSubmission,
        submitQueryBase,
    ]);
    const updateLocalStatePostStream = useCallback((_message) => {
        postFinalResponseClientSideLogic({
            isUserLoggedIn,
            submissionQueryCount,
            session: session ?? undefined,
            track,
            sendQueryEventSingular,
            maybeSendNthQueryEventSingular,
            refetchHomepageUpsells,
        });
    }, [
        refetchHomepageUpsells,
        isUserLoggedIn,
        sendQueryEventSingular,
        maybeSendNthQueryEventSingular,
        session,
        submissionQueryCount,
        track,
    ]);
    // Every consumer of this context re-rendered whenever the provider did,
    // including on each streamed token; keep the value stable while its parts are.
    const value = useMemo(() => ({
        submitQuery,
        quote,
        quoteForSubmission,
        followUps,
        quoteAttachment,
        setQuote,
        updateLocalStatePostStream,
        submissionQueryCount,
    }), [
        submitQuery,
        quote,
        quoteForSubmission,
        followUps,
        quoteAttachment,
        setQuote,
        updateLocalStatePostStream,
        submissionQueryCount,
    ]);
    return _jsx(PplxQueryStateContext.Provider, { value: value, children: children });
};
//# sourceMappingURL=AskQueryStateProvider.js.map